<template>
  <SCAButton
    v-if="routeTemplateId && canCreate(getTemplatePermissions(routeTemplateId))"
    :class="$style.action"
    size="small"
    type="ghost"
    icon-left="phosphor/plus"
    :disabled="!canCreateRecordsByModuleTag(module?.builtinModule)"
    @click.stop.prevent="redirectToCreate"
  >
    {{ label }}
  </SCAButton>
  <SCAButton
    :class="$style.action"
    size="small"
    type="ghost"
    icon-left="phosphor/star"
    :disabled="!canFavorite"
    @click.stop.prevent="handleToggleFavorite"
  >
    {{
      layoutStore.isFavorite(props.favoriteKey) ? $t("common.action.removeFavorites") : $t("common.action.addFavorites")
    }}
  </SCAButton>
  <SCAButton
    v-if="url"
    :class="$style.action"
    size="small"
    type="ghost"
    icon-left="phosphor/link"
    @click.stop.prevent="copyToClipboard"
  >
    {{ $t("common.action.copyLink") }}
  </SCAButton>
  <SCAButton
    v-if="url"
    :class="$style.action"
    size="small"
    type="ghost"
    icon-left="phosphor/arrow-square-out"
    @click.stop.prevent="handleWindowOpen"
  >
    {{ $t("common.action.openNew") }}
  </SCAButton>
</template>
<script setup lang="ts">
import type { PermissionCRUDH } from "@scayla/api-client-fetch";
import { SCAButton } from "@scayla/components";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import type { RouteLocationRaw } from "vue-router";
import { useRouter } from "vue-router";

import { Routes } from "@/router";
import { useLayoutStore } from "@/store/layoutStore";
import { useWorkspace } from "@/store/workspace";
import { canCreateRecordsByModuleTag } from "@/utils/moduleUtils";
import { showToastForError, showToastForSuccess } from "@/utils/toast";

const { t: $t } = useI18n();

const props = withDefaults(
  defineProps<{
    to?: RouteLocationRaw;
    favoriteKey?: string;
    moduleLabel?: string;
    templateLabel?: string;
    canFavorite?: boolean;
  }>(),
  {
    to: undefined,
    favoriteKey: "",
    moduleLabel: "",
    templateLabel: "",
    canFavorite: true,
  },
);

const router = useRouter();
const layoutStore = useLayoutStore();
const workspace = useWorkspace();

// ------------
// Permissions
// ------------
const getTemplatePermissions = workspace.getTemplatePermissions;

function canCreate(permissions: PermissionCRUDH) {
  return permissions.read && permissions.create;
}

// Cache router.resolve to avoid expensive recalculation on every render
const cachedUrl = ref<string | undefined>(undefined);
watch(
  () => props.to,
  (newTo) => {
    if (!newTo) {
      cachedUrl.value = undefined;
      return;
    }
    // Not every nav route carries its own ?tenant= — without it, a full page load in a new tab
    // can land on the wrong tenant and bounce off a permission-gated route.
    const resolved =
      typeof newTo === "object" ? { ...newTo, query: { tenant: workspace.tenantId, ...newTo.query } } : newTo;
    cachedUrl.value = router.resolve(resolved)?.href;
  },
  { immediate: true },
);

const url = computed(() => cachedUrl.value);

const routeModuleId = computed(() => {
  if (typeof props.to === "object" && "params" in props.to && props.to.params) {
    return props.to.params.moduleId as string | undefined;
  }
  return undefined;
});

const module = computed(() => {
  if (routeModuleId.value) {
    return workspace.modules.find((m) => m.id === routeModuleId.value);
  }

  return undefined;
});

const routeTemplateId = computed(() => {
  if (typeof props.to === "object" && props.to.query) {
    return props.to.query.templateId as string | undefined;
  }
  return undefined;
});

async function redirectToCreate() {
  if (routeModuleId.value && routeTemplateId.value) {
    await router.push({
      name: Routes.RECORD_CREATE,
      params: { moduleId: routeModuleId.value, templateId: routeTemplateId.value },
      query: {},
    });
  }
}

const label = computed(() => {
  if (props.moduleLabel && props.templateLabel) {
    return `${props.moduleLabel} > ${props.templateLabel}`;
  }
  return props.moduleLabel || props.templateLabel || "";
});

function handleWindowOpen() {
  if (cachedUrl.value) {
    window.open(cachedUrl.value, "_blank", "noopener");
  }
}

async function copyToClipboard() {
  if (cachedUrl.value) {
    const fullUrl = window.location.origin + cachedUrl.value;
    try {
      await navigator.clipboard.writeText(fullUrl);
      showToastForSuccess($t("common.action.linkCopied"));
    } catch (err) {
      showToastForError(err);
    }
  }
}

async function handleToggleFavorite() {
  if (!props.favoriteKey) return;
  await layoutStore.setFavorite(props.favoriteKey, !layoutStore.isFavorite(props.favoriteKey));
}
</script>
<style lang="scss" module>
.action {
  padding: var(--space-component-padding-null) var(--space-component-padding-xSmall) !important;
  text-align: left;
  color: var(--color-semantic-neutral-m3) !important;
  height: var(--dimension-400) !important;
  transition: background-color 0.25s ease-in-out;

  svg {
    color: var(--color-action-neutral-normal) !important;
  }

  &:hover {
    background-color: #f5f5f5;
    cursor: pointer;
  }
}
</style>
